import { useState, useCallback, useRef, useEffect } from 'react'

export type GateState = 'closed' | 'listening' | 'open' | 'responding' | 'paused' | 'awaiting_confirmation'

interface GateConfig {
  defaultState: GateState
  wakeWords: string[]
  sleepWords: string[]
  actionConfirmation: boolean
}

export function useGate(config: GateConfig) {
  const [state, setState] = useState<GateState>(config.defaultState)
  // Reset to defaultState when config changes (e.g. modeConfig loads after init)
  const prevDefaultRef = useRef(config.defaultState)
  useEffect(() => {
    if (config.defaultState !== prevDefaultRef.current) {
      prevDefaultRef.current = config.defaultState
      setState(config.defaultState)
    }
  }, [config.defaultState])
  const lastTranscriptRef = useRef('')
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const normalize = (text: string) => text.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim()

  const checkWords = useCallback((text: string, words: string[]) => {
    const norm = normalize(text)
    return words.some((w) => norm.includes(normalize(w)))
  }, [])

  const processTranscript = useCallback((text: string) => {
    lastTranscriptRef.current = text

    // Clear silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
    }

    if (state === 'closed' || state === 'listening') {
      if (checkWords(text, config.wakeWords)) {
        setState('open')
      }
      return
    }

    if (state === 'open') {
      if (checkWords(text, config.sleepWords)) {
        setState('closed')
        return
      }
      // No auto-close — gate stays open until explicit sleep word
    }

    if (state === 'responding') {
      if (checkWords(text, config.sleepWords)) {
        setState('closed')
        return
      }
    }

    if (state === 'awaiting_confirmation') {
      const norm = normalize(text)
      if (/\b(yes|proceed|confirm|do it|go ahead|ok)\b/.test(norm)) {
        setState('open') // confirmation received, proceed
      } else if (/\b(no|cancel|stop|nope)\b/.test(norm)) {
        setState('closed') // cancelled
      }
    }

    if (state === 'paused') {
      if (checkWords(text, config.wakeWords)) {
        setState('open')
      }
    }
  }, [state, config, checkWords])

  const setResponding = useCallback(() => {
    setState('responding')
  }, [])

  const setAwaitingConfirmation = useCallback(() => {
    setState('awaiting_confirmation')
  }, [])

  const setPaused = useCallback(() => {
    setState('paused')
  }, [])

  const setClosed = useCallback(() => {
    setState('closed')
  }, [])

  useEffect(() => {
    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
      }
    }
  }, [])

  return {
    state,
    processTranscript,
    setResponding,
    setAwaitingConfirmation,
    setPaused,
    setClosed,
  }
}
